import { useState } from 'react';
import { Button, DialogContent } from '@mui/material';
import useCurrentTerminal from '@/Hooks/useCurrentTerminal';
import ApplicationSpinner from '@/Infrastructure/Components/Loading/ApplicationSpinner';
import { getApiErrorMessage } from '@api/getApiError';
import { StreetTurn } from '@api/Trinium/Endpoints/StreetTurn/types';
import { useFetchStreetTurnQuery, usePutStreetTurnMutation } from '@api/Trinium/Endpoints/StreetTurn/slice';
import StreetTurnDataGrid from '@components/DataGrid/StreetTurn/Grid';
import ApplicationModal from '@components/Modals/ApplicationModal';
import { useModalProvider } from '@components/Modals/ModalContext';
import StreetTurnHeader from '@components/Modals/Headers/StreetTurnHeader';
import { StreetTurnProps } from './types';

import styles from './StreetTurn.module.css';

export default function StreetTurnModal({ legId }: StreetTurnProps) {
	const { number: terminalNumber } = useCurrentTerminal();
	const { queueModalAnnouncement } = useModalProvider();
	const [isOpen, setIsOpen] = useState(true);
	const [selectedStreetTurn, setSelectedStreetTurn] = useState<StreetTurn | null>();
	const [putStreetTurn, { isLoading: pendingSaveRequest }] = usePutStreetTurnMutation();

	const { data, isFetching } = useFetchStreetTurnQuery(
		{
			terminalNumber,
			legId
		},
		{
			skip: !legId,
			refetchOnMountOrArgChange: true
		}
	);

	const handleExecuteStreetTurnClick = () => {
		if (selectedStreetTurn) {
			void putStreetTurn({
				terminalNumber,
				legId: legId,
				actionType: {
					action: 'performStreetTurn',
					data: {
						importLegId: selectedStreetTurn.legId
					}
				}
			})
				.unwrap()
				.then(() => {
					setIsOpen(false);
				})
				.catch((error: unknown) => {
					const errorMessage = getApiErrorMessage(error);

					queueModalAnnouncement({
						severity: 'error',
						message: errorMessage
					});
				});
		}
	};

	const onStreetTurnSelect = (streetTurn: StreetTurn | null) => {
		setSelectedStreetTurn(streetTurn);
	};

	return (
		<ApplicationModal
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
			}}
			title='Initiate Street Turn'
			actions={
				<Button
					variant='contained'
					loading={pendingSaveRequest}
					disabled={!selectedStreetTurn || pendingSaveRequest}
					onClick={handleExecuteStreetTurnClick}
				>
					Save
				</Button>
			}
		>
			<StreetTurnHeader
				terminalNumber={terminalNumber}
				legId={legId}
			/>

			<DialogContent className={styles['street-turn-content']}>
				{isFetching ? (
					<ApplicationSpinner />
				) : (
					<StreetTurnDataGrid
						rows={data ?? []}
						onRowSelect={onStreetTurnSelect}
					/>
				)}
			</DialogContent>
		</ApplicationModal>
	);
}
